import { deepClone, isArray, toTree, treeToArr } from '@bd/vue3-utils'
import { computed, reactive, ref, shallowReactive, shallowRef, toRaw, toRef, watchEffect } from 'vue'
import type { TypeProps, IDictData, IDict, ISchema, ITreeDict, ITreeDictData, ISchemaItem } from '../types'
interface IDictConfig {
  dataIndex: string
  valueKey?: string
  nameKey?: string
  isTree?: boolean
  childrenKey?: string
  pValueKey?: string
}
type DictQueueItem = [BD.PromiseFn, IDictConfig, boolean]
export const useBdForm = (schema: ISchema) => {
  const _dictQueue = new Map<string, DictQueueItem>()
  const dictMap = ref(new Map<string, IDictData[]>())
  const dictOriginalMap = shallowRef(new Map<string, any[]>())
  const modelRef = reactive<Record<string, any>>({})
  const modelDefault = shallowReactive<Record<string, any>>({})
  const rulesRef = reactive<Record<string, any>>({})
  const formSchema = reactive<any[]>([])
  const schemaMap = reactive<Record<string, any>>({})

  /**
   * @des 显示feedback的item类型
   */
  const feedbackVisible = new Set([
    'select',
    'selects',
    'treeSelect',
    'treeSelects',
    'text',
    'number',
    'time',
    'date',
    'dateTime',
    'dateRange',
    'cascader',
    'tagsInput',
    'password'
  ])

  /**
   * @des 默认placeholder或者message前缀
   */
  const _formatPrefix = (type: string, title: string) => {
    let _key = ''
    const _word = new Map<string, Set<string>>([
      ['请输入', new Set(['text', 'textarea', 'number', 'tagsInput', 'password'])],
      ['请选择', new Set(['select', 'selects', 'treeSelect', 'treeSelects', 'time', 'date', 'dateTime', 'cascader'])]
    ])
    _word.forEach((item, key) => (item.has(type) ? (_key = key) : ''))
    return _key ? _key + title : ''
  }
  /**
   * @des 根据config格式化placeholder
   */
  const formatPlaceholder = (type?: TypeProps, title?: string, placeholder?: string) => {
    if (!type || !title) return
    return placeholder ?? _formatPrefix(type, title)
  }
  /**
   * @des 根据config格式化dict
   */
  const _formatDict = ({ data, valueKey = 'value', nameKey = 'label' }: IDict) => {
    return (data as IDictData[]).map(item => ({
      label: item[nameKey],
      value: item[valueKey]
    }))
  }
  /**
   * @des 根据config格式化treedict
   */
  const _formatTreeDict: (data: ITreeDict) => ITreeDictData[] = ({
    data,
    valueKey = 'value',
    nameKey = 'label',
    childrenKey = 'children',
    pValueKey = 'pId',
    isTree = false
  }) => {
    let _data = isTree ? treeToArr(data as any[], childrenKey) : data
    _data = (_data as any[]).map(item => ({
      label: item[nameKey],
      value: item[valueKey],
      ...item
    }))
    return toTree({
      data: _data,
      idName: valueKey,
      parentIdName: pValueKey,
      childrenName: 'children'
    })
  }
  const _setDictData = (dataIndex: any, originData: any, data: any) => {
    dictOriginalMap.value.set(dataIndex, originData)
    dictMap.value.set(dataIndex, data)
  }
  const executeDictQueue = () => {
    if (_dictQueue.size) {
      const _fns: { dataIndex: string; fn: any }[] = []
      _dictQueue.forEach((item, key) => {
        dictOriginalMap.value.delete(key)
        dictMap.value.delete(key)
        _fns.push({
          dataIndex: key,
          fn: item[0]
        })
      })
      Promise.all(_fns.map(e => e.fn())).then(res =>
        res.forEach((originData, index) => {
          const dataIndex = _fns[index].dataIndex
          const _dict = _dictQueue.get(dataIndex)
          const _isTree = _dict?.[2]
          const _args = _dict?.[1]
          const data = (!_isTree ? _formatDict : _formatTreeDict)({
            data: originData,
            ..._args
          })
          _setDictData(dataIndex, originData, data)
        })
      )
    }
  }
  const _handleSelect = (dict: IDict | ITreeDict, dataIndex: string, isTree: boolean) => {
    const { data, ...args } = dict
    if (isArray(data)) {
      dictMap.value.set(dataIndex, (!isTree ? _formatDict : _formatTreeDict)(dict))
      dictOriginalMap.value.set(dataIndex, dict.data)
    } else {
      _dictQueue.set(dataIndex, [data, { dataIndex, ...args }, isTree])
    }
  }
  const registerUnset = (dataIndex: string, unset: ISchemaItem['unset']) => {
    watchEffect(() => {
      if (modelRef[dataIndex] === unset) {
        modelRef[dataIndex] = undefined
      }
    })
  }

  const getRules = config => {
    const _rules: any[] = config.rules ? [...config.rules] : []
    const _isRequiredRuleExit = config.rules && config.rules.some(item => item['required'])
    if (config.required && !_isRequiredRuleExit) {
      _rules.push({ required: true, message: `${config.title}必填` })
    }
    return _rules
  }
  const registerShow = (dataIndex: string, show: ISchemaItem['show']) => {
    // watchEffect(() => {
    //   const _show = typeof show === 'function' ? show({ modelRef, record: toRef(modelRef, dataIndex) }) : show
    //   formSchema.forEach(item => {
    //     if (item.dataIndex === dataIndex) {
    //       item.show = _show
    //     }
    //   })
    //   if (!(isRef(_show) ? _show.value : _show)) {
    //     modelRef[dataIndex] = undefined
    //     rulesRef[dataIndex] = undefined
    //   } else {
    //     rulesRef[dataIndex] = schemaMap[dataIndex].rules
    //   }
    //   console.log(formSchema)
    // })
    const _show = computed(() => (typeof show === 'function' ? show({ modelRef, record: toRef(modelRef, dataIndex) }) : show))
    watchEffect(() => {
      if (_show.value === true) {
        rulesRef[dataIndex] = getRules(schemaMap[dataIndex])
      } else {
        modelRef[dataIndex] = undefined
        rulesRef[dataIndex] = undefined
      }
    })
    return _show
  }
  const initForm = () => {
    for (let i = 0; i < schema.length; ++i) {
      const item = schema[i]
      schemaMap[item.dataIndex] = item
      const { dataIndex, show, ...args } = item
      const type = args.render ? undefined : args.type ?? 'text'
      rulesRef[dataIndex] = getRules(item)
      modelRef[dataIndex] = item.default
      if (item.unset) registerUnset(dataIndex, item.unset)
      if (type === 'select' || type === 'selects' || type === 'radio' || type === 'checkbox') {
        _handleSelect(args.dict as IDict, dataIndex, false)
      } else if (type === 'cascader' || type === 'cascaders') {
        _handleSelect(args.dict as IDict, dataIndex, true)
      } else if (type === 'treeSelect' || type === 'treeSelects') {
        _handleSelect(args.dict as ITreeDict, dataIndex, true)
      }
      formSchema.push({
        dataIndex,
        placeholder: formatPlaceholder(type, args.title, args.placeholder),
        show: show ? registerShow(dataIndex, show) : true,
        type,
        ...args
      })
    }
    Object.assign(modelDefault, {}, toRaw(modelRef))
    executeDictQueue()
  }
  const resetFields = () => {
    Object.assign(modelRef, {}, deepClone(modelDefault))
  }
  return {
    feedbackVisible,
    executeDictQueue,
    dictMap,
    dictOriginalMap,
    modelRef,
    rulesRef,
    initForm,
    formSchema,
    resetFields
  }
}
